import React, { useState, useMemo } from 'react';
import {
  useUsers,
  useUserStats,
  useCreateUser,
  useUpdateUserOrganization,
  useMemberOpenTicketCount,
  useAssignableUsers
} from '../hooks/useUsers';
import { MemberOffboardDialog, OpenTicketHandoffField } from './MemberOffboardDialog';
import { describeHandoff, toOpenTicketHandoff, type HandoffChoice } from '../lib/memberHandoff';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/apiErrors';
import {
  Plus,
  Edit,
  UserMinus,
  Users,
  Search,
  AlertCircle,
  X,
  Loader2
} from 'lucide-react';
import { UserSettingsSkeleton } from './LoadingSkeleton';
import type { UserDto, CreateUserRequest, UpdateUserOrganizationRequest } from '../types/users';

interface CreateUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  role: 'Admin' | 'Agent';
  isAssignable: boolean;
}

interface EditUserFormData {
  role: 'Admin' | 'Agent';
  isActive: boolean;
  isAssignable: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface UserSettingsProps {
  organizationId?: string;
}

export const UserSettings: React.FC<UserSettingsProps> = ({ organizationId }) => {
  // Pass organizationId to all hooks for multi-org support
  // This ensures we're managing users for the selected organization, not the JWT organization
  const { data, isLoading, error } = useUsers(undefined, organizationId);
  const { data: stats } = useUserStats(organizationId);
  const createUserMutation = useCreateUser(organizationId);
  const updateUserOrgMutation = useUpdateUserOrganization(organizationId);
  const { data: assignableUsers = [] } = useAssignableUsers(organizationId);

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);
  const [offboardingUser, setOffboardingUser] = useState<UserDto | null>(null);
  // Who takes over the edited member's tickets when the edit deactivates them
  const [editHandoff, setEditHandoff] = useState<HandoffChoice>('');

  const [createFormData, setCreateFormData] = useState<CreateUserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Agent',
    isAssignable: true
  });
  const [editFormData, setEditFormData] = useState<EditUserFormData>({
    role: 'Agent',
    isActive: true,
    isAssignable: true
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const resetCreateForm = () => {
    setCreateFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'Agent',
      isAssignable: true
    });
    setFormErrors({});
  };

  const resetEditForm = () => {
    setEditFormData({
      role: 'Agent',
      isActive: true,
      isAssignable: true
    });
    setEditHandoff('');
    setFormErrors({});
  };

  // Turning Active off in the edit dialog deactivates the member, so their open tickets need a new owner first
  const editingMembership = editingUser?.organizations?.find(org => org.organizationId === organizationId);
  const isEditDeactivating = !!editingMembership?.isActive && !editFormData.isActive;
  const { data: editOpenTicketCount = 0 } = useMemberOpenTicketCount(editingUser?.id, organizationId, isEditDeactivating);

  const validateCreateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!createFormData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (createFormData.firstName.trim().length > 50) {
      errors.firstName = 'First name must be 50 characters or less';
    }

    if (!createFormData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (createFormData.lastName.trim().length > 50) {
      errors.lastName = 'Last name must be 50 characters or less';
    }

    if (!createFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreateForm()) {
      return;
    }

    try {
      const userData: CreateUserRequest = {
        firstName: createFormData.firstName.trim(),
        lastName: createFormData.lastName.trim(),
        email: createFormData.email.trim().toLowerCase(),
        role: createFormData.role,
        isActive: true,
        isAssignable: createFormData.isAssignable
      };

      // An existing account keeps its own name, so use what the server returned
      const addedUser = await createUserMutation.mutateAsync(userData);

      toast({
        title: "Success",
        description: `${addedUser.fullName} has been added to the organization.`,
      });

      setShowCreateDialog(false);
      resetCreateForm();
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to add user');
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleEdit = async () => {
    if (!editingUser) return;

    try {
      const updateData: UpdateUserOrganizationRequest = {
        role: editFormData.role,
        isActive: editFormData.isActive,
        isAssignable: editFormData.isAssignable,
        ...(isEditDeactivating && editOpenTicketCount > 0 ? toOpenTicketHandoff(editHandoff) : {})
      };

      const result = await updateUserOrgMutation.mutateAsync({
        userId: editingUser.id,
        orgData: updateData
      });

      toast({
        title: "Success",
        description: "User updated successfully." + describeHandoff(
          result.handedOffTicketCount,
          editHandoff,
          assignableUsers.find(u => u.id === editHandoff)?.fullName
        ),
      });

      setShowEditDialog(false);
      setEditingUser(null);
      resetEditForm();
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to update user');
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (user: UserDto) => {
    // Find the user's role in this organization
    const orgRelationship = user.organizations?.find(
      org => org.organizationId === organizationId
    );

    setEditingUser(user);
    setEditHandoff('');
    setEditFormData({
      role: (orgRelationship?.role as 'Admin' | 'Agent') || 'Agent',
      isActive: orgRelationship?.isActive ?? true,
      isAssignable: orgRelationship?.isAssignable ?? true
    });
    setShowEditDialog(true);
  };

  const openCreateDialog = () => {
    resetCreateForm();
    setShowCreateDialog(true);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'Agent':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get users and filter by search term
  const users = useMemo(() => {
    const allUsers = data?.users || [];
    if (!searchTerm) return allUsers;

    const term = searchTerm.toLowerCase();
    return allUsers.filter(user =>
      user.fullName.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      user.firstName.toLowerCase().includes(term) ||
      user.lastName.toLowerCase().includes(term)
    );
  }, [data?.users, searchTerm]);

  // Get user's role for current organization
  const getUserRole = (user: UserDto): string => {
    const orgRelationship = user.organizations?.find(
      org => org.organizationId === organizationId
    );
    return orgRelationship?.role || 'Unknown';
  };

  // Get user's active status for current organization
  const getUserActiveStatus = (user: UserDto): boolean => {
    const orgRelationship = user.organizations?.find(
      org => org.organizationId === organizationId
    );
    return orgRelationship?.isActive ?? true;
  };

  // Get joined date for current organization
  const getUserJoinedDate = (user: UserDto): string | null => {
    const orgRelationship = user.organizations?.find(
      org => org.organizationId === organizationId
    );
    return orgRelationship?.joinedAt || null;
  };

  if (isLoading) {
    return <UserSettingsSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">{getErrorMessage(error, "Failed to load users. Please try again.")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold">User Management</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Manage users and their roles within this organization.
          </p>
        </div>

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add User to Organization</DialogTitle>
              <DialogDescription>
                Add a new user or invite an existing user to this organization.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={createFormData.firstName}
                  onChange={(e) => {
                    setCreateFormData(prev => ({ ...prev, firstName: e.target.value }));
                    if (formErrors.firstName) {
                      setFormErrors(prev => ({ ...prev, firstName: undefined }));
                    }
                  }}
                  placeholder="John"
                  className={formErrors.firstName ? 'border-red-500' : ''}
                />
                <div className="flex justify-between">
                  <div>
                    {formErrors.firstName && (
                      <p className="flex items-center gap-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4" />
                        {formErrors.firstName}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {createFormData.firstName.length}/50
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={createFormData.lastName}
                  onChange={(e) => {
                    setCreateFormData(prev => ({ ...prev, lastName: e.target.value }));
                    if (formErrors.lastName) {
                      setFormErrors(prev => ({ ...prev, lastName: undefined }));
                    }
                  }}
                  placeholder="Doe"
                  className={formErrors.lastName ? 'border-red-500' : ''}
                />
                <div className="flex justify-between">
                  <div>
                    {formErrors.lastName && (
                      <p className="flex items-center gap-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4" />
                        {formErrors.lastName}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {createFormData.lastName.length}/50
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => {
                    setCreateFormData(prev => ({ ...prev, email: e.target.value }));
                    if (formErrors.email) {
                      setFormErrors(prev => ({ ...prev, email: undefined }));
                    }
                  }}
                  placeholder="john.doe@example.com"
                  className={formErrors.email ? 'border-red-500' : ''}
                />
                {formErrors.email && (
                  <p className="flex items-center gap-1 text-sm text-red-500">
                    <AlertCircle className="h-4 w-4" />
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Label>Role</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="role"
                      value="Admin"
                      checked={createFormData.role === 'Admin'}
                      onChange={() => setCreateFormData(prev => ({ ...prev, role: 'Admin' }))}
                      className="h-4 w-4"
                    />
                    <div>
                      <p className="font-medium">Admin</p>
                      <p className="text-sm text-muted-foreground">
                        Full access including user management and settings
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="role"
                      value="Agent"
                      checked={createFormData.role === 'Agent'}
                      onChange={() => setCreateFormData(prev => ({ ...prev, role: 'Agent' }))}
                      className="h-4 w-4"
                    />
                    <div>
                      <p className="font-medium">Agent</p>
                      <p className="text-sm text-muted-foreground">
                        Can manage tickets and customers
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="createAssignableStatus" className="font-medium">Available for Assignment</Label>
                  <p className="text-sm text-muted-foreground">
                    When disabled, this user won't appear in ticket assignment dropdowns
                  </p>
                </div>
                <Switch
                  id="createAssignableStatus"
                  checked={createFormData.isAssignable}
                  onCheckedChange={(checked) =>
                    setCreateFormData(prev => ({ ...prev, isAssignable: checked }))
                  }
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  If a user with this email already exists, they will be added to this organization with the selected role.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={
                  !createFormData.firstName.trim() ||
                  !createFormData.lastName.trim() ||
                  !createFormData.email.trim() ||
                  createUserMutation.isPending ||
                  Object.keys(formErrors).length > 0
                }
              >
                {createUserMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add User'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="text-xs sm:text-sm">
            {stats.totalUsers} total users
          </Badge>
          <Badge variant="outline" className="text-xs sm:text-sm">
            {stats.activeUsers} active
          </Badge>
          {stats.inactiveUsers > 0 && (
            <Badge variant="outline" className="text-xs sm:text-sm text-muted-foreground">
              {stats.inactiveUsers} inactive
            </Badge>
          )}
        </div>
      )}

      {/* Search */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center space-x-2">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="relative flex-1 max-w-sm">
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-8"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* User List Card */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
              Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {users.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-muted-foreground">
                <Users className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                <p className="text-sm sm:text-base">No users found</p>
                <p className="text-xs sm:text-sm">Add users to this organization to get started</p>
              </div>
            ) : (
              <>
                {/* Mobile Card Layout */}
                <div className="space-y-3 md:hidden">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="border rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {user.initials}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{user.fullName}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEditDialog(user)}
                            disabled={updateUserOrgMutation.isPending}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => setOffboardingUser(user)}
                            title="Deactivate or remove"
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={getRoleColor(getUserRole(user))}>
                          {getUserRole(user)}
                        </Badge>
                        <Badge variant={getUserActiveStatus(user) ? 'default' : 'secondary'}>
                          {getUserActiveStatus(user) ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {getUserJoinedDate(user) && (
                        <p className="text-xs text-muted-foreground">
                          Joined {formatDate(getUserJoinedDate(user)!)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table Layout */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                                {user.initials}
                              </div>
                              <span className="font-medium">{user.fullName}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {user.email}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleColor(getUserRole(user))}>
                              {getUserRole(user)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getUserActiveStatus(user) ? 'default' : 'secondary'}>
                              {getUserActiveStatus(user) ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {getUserJoinedDate(user) ? formatDate(getUserJoinedDate(user)!) : '—'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(user)}
                                disabled={updateUserOrgMutation.isPending}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setOffboardingUser(user)}
                                title="Deactivate or remove"
                              >
                                <UserMinus className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user's role and status in this organization.
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                  {editingUser.initials}
                </div>
                <div>
                  <p className="font-medium">{editingUser.fullName}</p>
                  <p className="text-sm text-muted-foreground">{editingUser.email}</p>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Organization Role</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="editRole"
                      value="Admin"
                      checked={editFormData.role === 'Admin'}
                      onChange={() => setEditFormData(prev => ({ ...prev, role: 'Admin' }))}
                      className="h-4 w-4"
                    />
                    <div>
                      <p className="font-medium">Admin</p>
                      <p className="text-sm text-muted-foreground">
                        Full access including user management and settings
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="editRole"
                      value="Agent"
                      checked={editFormData.role === 'Agent'}
                      onChange={() => setEditFormData(prev => ({ ...prev, role: 'Agent' }))}
                      className="h-4 w-4"
                    />
                    <div>
                      <p className="font-medium">Agent</p>
                      <p className="text-sm text-muted-foreground">
                        Can manage tickets and customers
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="activeStatus" className="font-medium">Active Status</Label>
                  <p className="text-sm text-muted-foreground">
                    Inactive users cannot access this organization
                  </p>
                </div>
                <Switch
                  id="activeStatus"
                  checked={editFormData.isActive}
                  onCheckedChange={(checked) =>
                    setEditFormData(prev => ({ ...prev, isActive: checked }))
                  }
                />
              </div>

              {isEditDeactivating && (
                <OpenTicketHandoffField
                  userId={editingUser.id}
                  organizationId={organizationId}
                  openTicketCount={editOpenTicketCount}
                  value={editHandoff}
                  onChange={setEditHandoff}
                />
              )}

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="assignableStatus" className="font-medium">Available for Assignment</Label>
                  <p className="text-sm text-muted-foreground">
                    When disabled, this user won't appear in ticket assignment dropdowns
                  </p>
                </div>
                <Switch
                  id="assignableStatus"
                  checked={editFormData.isAssignable}
                  onCheckedChange={(checked) =>
                    setEditFormData(prev => ({ ...prev, isAssignable: checked }))
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setEditingUser(null);
                resetEditForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={updateUserOrgMutation.isPending || (isEditDeactivating && editOpenTicketCount > 0 && !editHandoff)}
            >
              {updateUserOrgMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MemberOffboardDialog
        user={offboardingUser}
        organizationId={organizationId}
        onOpenChange={(open) => { if (!open) setOffboardingUser(null); }}
      />
    </div>
  );
};
